import { useEffect, useState, useMemo } from "react";
import { skipReasonLabel } from "../lib/constants";
import { NAVY, GOLD } from "../styles/palette";
import { getAuthToken, SUPABASE_ANON_KEY, SUPABASE_URL } from "../lib/supabase";
import { RouteSkipModal } from "./RouteSkipModal";
import { JobDetailModal } from "./JobDetailModal";
import { ClickableDate } from "./ClickableDate";
import { localDateStr } from "../lib/date";
import { optimizeRoundTrip, fetchOptimizedRoute } from "../lib/route";
import { fetchClientAvgMap } from "../lib/visitTimes";
import { useIsMobile } from "../lib/useIsMobile";
import { useBackClose } from "../lib/navStack";

// ═══════════════════════════════════════════════════════════════════
// TECHNICIAN ROUTES
// ═══════════════════════════════════════════════════════════════════
// Operator-facing dashboard: pick a date + one-or-many technicians,
// see each tech's jobs for that day grouped by tech. Each tech section
// is its own card with its own progress bar. Click any job to open
// JobDetailModal (same single source of truth as the calendar view).
//
// Multi-tech selection by toggle chips (cheap + visual). Date wrapped
// in a div that calls input.showPicker() so clicking anywhere on the
// field opens the date selector — not just the tiny calendar icon.
// ═══════════════════════════════════════════════════════════════════

export function MyRoutePanel({ technicians, clients, invoices, pricing, serviceArea, onRefresh, showToast, today, selectedTechId, onRefreshJobsDone, techScoped = false, selectedDate, onDateChange, selectedTechnician, onTechnicianChange, comparisonIds = null, onComparisonChange }) {
  // techScoped = the logged-in user IS a technician (not an operator). In that
  // mode the panel shows ONLY their own route: no tech selector, no Unassigned
  // bucket, no reassignment. RLS (sql/022) is the real enforcement; this is the
  // matching UX + defense-in-depth so the office-only surfaces aren't even shown.
  // Initial selection: the prop-passed tech if any, otherwise all active techs
  const initialIds = selectedTechId
    ? [selectedTechId]
    : (technicians || []).filter(t => t.active).map(t => t.id);

  const isMobile = useIsMobile();
  const [localTechIds,    setSelectedTechIds] = useState(initialIds);
  const [localRouteDate,  setLocalRouteDate]  = useState(localDateStr(today));
  const routeDate = selectedDate === undefined ? localRouteDate : selectedDate;
  const controlledTech = !techScoped && selectedTechnician !== undefined;
  const controlledDate = selectedDate !== undefined;
  const comparing = controlledTech && Array.isArray(comparisonIds);
  const selectedTechIds = comparing ? comparisonIds.filter(id => id !== "__unassigned__") : controlledTech
    ? selectedTechnician === "all" ? (technicians || []).filter(t => t.active).map(t => t.id)
      : selectedTechnician === "unassigned" ? [] : [selectedTechnician]
    : localTechIds;
  const showUnassigned = !techScoped && (comparing ? comparisonIds.includes("__unassigned__") : !controlledTech || selectedTechnician === "all" || selectedTechnician === "unassigned");
  const comparedIds = comparing ? comparisonIds : [...selectedTechIds, ...(showUnassigned ? ["__unassigned__"] : [])];
  function toggleComparison(id) {
    const next = comparedIds.includes(id) ? comparedIds.filter(value => value !== id) : [...comparedIds, id];
    onComparisonChange && onComparisonChange(next);
  }
  function setRouteDate(value) {
    if (selectedDate === undefined) setLocalRouteDate(value);
    onDateChange && onDateChange(value);
  }
  const [allJobs,         setAllJobs]         = useState([]);
  const [skipModal,       setSkipModal]       = useState(null);
  const [jobDetailId,     setJobDetailId]     = useState(null);
  const [loading,         setLoading]         = useState(controlledDate);
  const [loadError,       setLoadError]       = useState("");
  const [loadRevision,    setLoadRevision]    = useState(0);
  const [clientAvg,       setClientAvg]       = useState({}); // {client_id:{avgMin,visits}} — GPS typical-clean per yard
  // iOS back-swipe / Back button closes the open sheet instead of leaving the tab.
  useBackClose(!!skipModal,   () => setSkipModal(null));
  useBackClose(!!jobDetailId, () => setJobDetailId(null));

  // GPS clean-time averages (one fetch; drives the "🧹 ~N min typical clean" hint per row).
  useEffect(() => { fetchClientAvgMap().then(setClientAvg); }, []);

  // Pull every job for the date, then filter client-side per selected
  // tech. One query per date keeps things simple — for big operations
  // we'd index + paginate, but for now this is fine.
  useEffect(() => {
    if (!routeDate) { setAllJobs([]); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (controlledDate) { setAllJobs([]); setLoadError(""); }
      try {
        // B1: in tech-scoped mode, request ONLY this technician's jobs. (RLS also
        // enforces this, but scoping the query keeps the payload tight and the
        // UI honest.)
        const techFilter = techScoped && selectedTechId ? `&technician_id=eq.${selectedTechId}` : "";
        const url = `${SUPABASE_URL}/rest/v1/jobs?scheduled_date=eq.${routeDate}&status=not.in.(cancelled,rescheduled)${techFilter}&order=client_name.asc&limit=500`;
        const response = await fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } });
        if (controlledDate && !response.ok) throw new Error("The routes could not be loaded. Please try again.");
        const res = await response.json();
        if (controlledDate && !Array.isArray(res)) throw new Error("The routes returned an unexpected response. Please try again.");
        if (!cancelled && Array.isArray(res)) setAllJobs(res);
      } catch (e) {
        if (controlledDate && !cancelled) setLoadError(e.message || "The routes could not be loaded. Please try again.");
        else console.error("Route load error:", e);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [routeDate, controlledDate, loadRevision]); // eslint-disable-line react-hooks/exhaustive-deps -- techScoped/selectedTechId are fixed per mount

  function toggleTech(id) {
    setSelectedTechIds(curr =>
      curr.includes(id) ? curr.filter(x => x !== id) : [...curr, id]
    );
  }

  function selectAllTechs() {
    setSelectedTechIds((technicians || []).filter(t => t.active).map(t => t.id));
  }
  function clearTechs() {
    setSelectedTechIds([]);
  }
  function selectOnly(id) {
    setSelectedTechIds([id]);
  }

  // ── Per-job actions (status only — don't overwrite tech assignment) ──
  async function patchJob(jobId, body) {
    await fetch(`${SUPABASE_URL}/rest/v1/jobs?id=eq.${jobId}`, {
      method: "PATCH",
      headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json", Prefer: "return=minimal" },
      body: JSON.stringify(body),
    });
    // Refetch
    const url = `${SUPABASE_URL}/rest/v1/jobs?scheduled_date=eq.${routeDate}&status=not.in.(cancelled,rescheduled)&order=client_name.asc&limit=500`;
    const res = await fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } }).then(r => r.json());
    if (Array.isArray(res)) setAllJobs(res);
  }

  async function handleStart(job) {
    const now = new Date().toISOString();
    await patchJob(job.id, { status: "started", started_at: now });
    showToast && showToast("Job started 🚀");
  }
  async function handleComplete(job) {
    const now = new Date().toISOString();
    await patchJob(job.id, {
      status:       "completed",
      completed_at: now,
      ...(!job.started_at ? { started_at: now } : {}),
    });
    showToast && showToast("Job completed ✅");
    onRefreshJobsDone && onRefreshJobsDone();
  }

  // Proof-of-service photo capture — the whole pipeline (private job-photos bucket,
  // org-scoped RLS, set_photo → job_headers.photo_url, CRM display, client MMS) already
  // existed; the web field app just had no way to CAPTURE one. Techs can now attach a
  // photo from their phone camera.
  async function handlePhoto(job, file) {
    if (!file) return;
    if (!(file.type || "").startsWith("image/")) { showToast && showToast("Please choose an image.", "error"); return; }
    try {
      showToast && showToast("Uploading photo…");
      const ext = ((file.type.split("/")[1]) || "jpg").replace("jpeg", "jpg");
      // Storage RLS (sql/028) gates the upload on the FIRST path segment being a job
      // that belongs to the tech's org — so it MUST be the service job id.
      const path = `${job.id}/proof-${Date.now()}.${ext}`;
      const up = await fetch(`${SUPABASE_URL}/storage/v1/object/job-photos/${path}`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": file.type || "image/jpeg", "x-upsert": "true" },
        body: file,
      });
      if (!up.ok) { const e = await up.text().catch(()=>""); showToast && showToast(`Photo upload failed${e?`: ${e.slice(0,90)}`:""}`, "error"); return; }
      // Canonical record on the job header (feeds the CRM Visits view + the client's
      // job-complete message), best-effort.
      await fetch(`${SUPABASE_URL}/rest/v1/rpc/job_transition`, {
        method: "POST",
        headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, "Content-Type": "application/json" },
        body: JSON.stringify({ p_service_id: job.id, p_action: "set_photo", p_payload: { photo_url: path } }),
      }).catch(()=>{});
      // Also stamp the service row so the route list + JobDetailModal reflect it (and refetch).
      await patchJob(job.id, { photo_url: path });
      showToast && showToast("Proof photo added 📸");
      onRefresh && onRefresh();
    } catch(e) { showToast && showToast(`Photo failed: ${e.message||e}`, "error"); }
  }

  // Build per-tech sections in the same order the user selected them
  const visibleTechs = (technicians || []).filter(t => selectedTechIds.includes(t.id));
  const jobsForTech = id => allJobs.filter(j => (j.technician_id || "") === id);
  // F4: a job assigned to a now-INACTIVE (or deleted) tech matches no active-tech
  // chip, so without this it would vanish from the board entirely. Surface those
  // alongside the genuinely-unassigned ones so they can be reassigned.
  const activeTechIdSet = new Set((technicians || []).filter(t => t.active).map(t => t.id));
  const unassignedJobs = allJobs.filter(j => !j.technician_id || !activeTechIdSet.has(j.technician_id));

  // ── Route ordering: order each tech's stops by the optimized round trip from
  // their home, using REAL driving distance (route-matrix) when available, else the
  // straight-line estimate. Falls back to the raw (name-sorted) order when a tech
  // has no home or fewer than 2 mappable stops. ────────────────────────────────
  const fin = (x) => Number.isFinite(Number(x));
  const clientCoords = useMemo(() => {
    const m = new Map();
    (clients || []).forEach(c => { if (fin(c.lat) && fin(c.lng)) m.set(c.id, { lat: Number(c.lat), lng: Number(c.lng), region: c.state || null }); });
    return m;
  }, [clients]);

  // Per-tech {home, stops} for techs that CAN be optimized (home + ≥2 mapped stops).
  const routeInputs = useMemo(() => {
    const out = {};
    visibleTechs.forEach(tech => {
      if (!fin(tech.home_lat) || !fin(tech.home_lng)) return;
      const seen = new Set(); const stops = [];
      jobsForTech(tech.id).forEach(j => {
        if (seen.has(j.client_id)) return;
        const c = clientCoords.get(j.client_id);
        // `id` is what optimize-route echoes back; `region` (state) only matters past 24
        // stops, where it stops a chunk straddling the river. client_id drives the ranking.
        if (c) { seen.add(j.client_id); stops.push({ id: j.client_id, client_id: j.client_id, lat: c.lat, lng: c.lng, region: c.region }); }
      });
      if (stops.length >= 2) out[tech.id] = { home: { lat: Number(tech.home_lat), lng: Number(tech.home_lng) }, stops };
    });
    return out;
  }, [visibleTechs, allJobs, clientCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  // Stable key so we only re-request when the stop SET changes (not on a status tap).
  // ONE optimize-route request per tech (see lib/route.js) — the old per-pair matrix
  // billed N-squared elements per call and was the source of a runaway Google bill.
  const matrixKey = useMemo(() =>
    Object.entries(routeInputs).map(([id, v]) => id + ":" + v.stops.map(s => s.client_id).sort().join(",")).join("|"),
    [routeInputs]);
  const [roadRoutes, setRoadRoutes] = useState({});
  const [reopt, setReopt] = useState("");   // tech id currently being re-optimized

  // Manual "Re-optimize" — for right after the operator moves clients between techs.
  // Two things it does that the automatic path does not do IMMEDIATELY:
  //   1. re-asks optimize-route for THIS tech now, and
  //   2. passes `persist`, writing route_seq onto job_headers so the tech's PHONE gets the new
  //      order at once instead of waiting for the refresh-route-plans cron (up to 10 min).
  // COST: no `refresh` flag is sent, so the edge function answers from its cache unless the
  // stop set genuinely changed. Pressing this on an unchanged route costs ZERO Google calls;
  // a real reassignment costs exactly one. The button is also disabled while in flight, so it
  // cannot be spam-clicked into a burst of requests.
  async function reoptimize(tech) {
    const input = routeInputs[tech.id];
    if (!input) { showToast && showToast(`${tech.name} has no mappable stops to optimize.`, "error"); return; }
    setReopt(tech.id);
    try {
      const authFetch = (url, opts = {}) => fetch(url, { ...opts, headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, ...(opts.headers || {}) } });
      const r = await fetchOptimizedRoute(input.home, input.stops, SUPABASE_URL, authFetch,
                                          { technician_id: tech.id, route_date: routeDate });
      if (r) {
        setRoadRoutes(prev => ({ ...prev, [tech.id]: r }));
        showToast && showToast(`${tech.name}'s route re-optimized — sent to their phone 🚗`);
      } else {
        // Null = the function failed or returned a partial answer; the panel keeps showing the
        // free straight-line order rather than a half-applied one.
        showToast && showToast(`Couldn't re-optimize ${tech.name}'s route — showing the estimated order.`, "error");
      }
    } catch (e) {
      showToast && showToast(`Re-optimize failed: ${e.message || e}`, "error");
    }
    setReopt("");
  }
  useEffect(() => {
    let alive = true;
    const entries = Object.entries(routeInputs);
    if (!entries.length) { setRoadRoutes({}); return; }
    const authFetch = (url, opts = {}) => fetch(url, { ...opts, headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}`, ...(opts.headers || {}) } });
    Promise.all(entries.map(([id, v]) => fetchOptimizedRoute(v.home, v.stops, SUPABASE_URL, authFetch).then(r => [id, r])))
      .then(results => { if (alive) setRoadRoutes(Object.fromEntries(results.filter(([, r]) => r))); });
    return () => { alive = false; };
  }, [matrixKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Return a tech's jobs in optimized visit order (cleanup + deo for one client stay
  // adjacent). Also returns the distance basis for the header badge.
  const orderedJobs = (tech, rawJobs) => {
    const input = routeInputs[tech.id];
    if (!input) return { jobs: rawJobs, basis: null };
    // Road-optimised when available; free straight-line estimate otherwise.
    const opt = roadRoutes[tech.id] || optimizeRoundTrip(input.home, input.stops);
    const rank = new Map(opt.ordered.map((s, i) => [s.client_id, i]));
    const jobs = rawJobs.map((j, i) => [j, i]).sort((a, b) => {
      const ra = rank.has(a[0].client_id) ? rank.get(a[0].client_id) : Infinity;
      const rb = rank.has(b[0].client_id) ? rank.get(b[0].client_id) : Infinity;
      return ra - rb || a[1] - b[1];
    }).map(x => x[0]);
    return { jobs, basis: opt.basis };
  };

  return (
    <div className="sc-routes">
      {/* ── Controls bar ──────────────────────────────────────────── */}
      <div className="card sc-route-controls" style={{marginBottom:14,padding:"14px 16px"}}>
        {/* Date + summary row */}
        <div style={{display:"flex",alignItems:"center",gap:14,flexWrap:"wrap",marginBottom:12}}>
          <ClickableDate value={routeDate} onChange={setRouteDate}/>
          <button className="btn btn-ghost btn-sm" onClick={()=>setRouteDate(localDateStr(today))}>Today</button>
          {loading
            ? <span style={{fontSize:12,color:"#8993a4"}}>Loading...</span>
            : <span style={{fontSize:12,color:"#8993a4"}}>{loadError ? "Routes unavailable" : `${allJobs.length} service${allJobs.length === 1 ? "" : "s"} scheduled on this date`}</span>}
        </div>

        {/* Tech selector — toggle chips (operator only; a technician sees just their own route) */}
        {controlledTech && (
          <label className="sc-route-technician-filter" style={{display:"flex",alignItems:"center",gap:10,fontSize:12,color:"var(--text2)"}}>
            Technician
            <select value={selectedTechnician} onChange={e => { onComparisonChange && onComparisonChange(null); onTechnicianChange && onTechnicianChange(e.target.value); }} disabled={!onTechnicianChange}>
              <option value="all">All technicians</option>
              <option value="unassigned">Unassigned</option>
              {(technicians || []).filter(t => t.active || t.id === selectedTechnician).map(t => <option key={t.id} value={t.id}>{t.name}{!t.active ? " (inactive)" : ""}</option>)}
            </select>
          </label>
        )}
        {controlledTech && onComparisonChange && (
          <details className="sc-route-comparison" style={{marginTop:12,borderTop:"1px solid var(--border)",paddingTop:10}}>
            <summary style={{cursor:"pointer",fontSize:13,fontWeight:600,color:"var(--text)",padding:"5px 0"}}>
              Compare technicians{comparing ? ` · ${selectedTechIds.length} selected${showUnassigned ? " + unassigned" : ""}` : ""}
            </summary>
            <div style={{paddingTop:8}}>
              <p style={{fontSize:12,color:"var(--text2)",marginBottom:10}}>Choose the routes you want to compare. This selection stays with Technician routes.</p>
              <div role="group" aria-label="Technicians to compare" style={{display:"flex",flexWrap:"wrap",gap:"8px 16px"}}>
                {(technicians || []).filter(t => t.active || comparedIds.includes(t.id)).map(tech => (
                  <label key={tech.id} style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:13,cursor:"pointer",minHeight:32}}>
                    <input type="checkbox" checked={comparedIds.includes(tech.id)} onChange={() => toggleComparison(tech.id)} />
                    {tech.name}{!tech.active ? " (inactive)" : ""}
                  </label>
                ))}
                <label style={{display:"inline-flex",alignItems:"center",gap:7,fontSize:13,cursor:"pointer",minHeight:32}}>
                  <input type="checkbox" checked={comparedIds.includes("__unassigned__")} onChange={() => toggleComparison("__unassigned__")} />Unassigned
                </label>
              </div>
              {comparing && <button className="btn btn-ghost btn-sm" style={{marginTop:10}} onClick={() => onComparisonChange(null)}>Use technician filter</button>}
            </div>
          </details>
        )}
        {!techScoped && !controlledTech && (
        <div>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:6}}>
            <span style={{fontSize:11,fontWeight:700,color:"#42526e",textTransform:"uppercase",letterSpacing:".05em"}}>Technicians</span>
            {/* Phone: these are real finger targets (36px), not 13px text. */}
            <button onClick={selectAllTechs} style={{background:"none",border:"none",cursor:"pointer",fontSize:isMobile?13:11,color:NAVY,fontWeight:600,padding:isMobile?"8px 10px":0,minHeight:isMobile?36:undefined}}>Select all</button>
            <span style={{color:"#dfe1e6"}}>·</span>
            <button onClick={clearTechs} style={{background:"none",border:"none",cursor:"pointer",fontSize:isMobile?13:11,color:"#8993a4",fontWeight:600,padding:isMobile?"8px 10px":0,minHeight:isMobile?36:undefined}}>Clear</button>
          </div>
          <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
            {(technicians || []).filter(t => t.active).map(t => {
              const on = selectedTechIds.includes(t.id);
              const count = jobsForTech(t.id).length;
              // Phones: an explicit "only" control next to the chip (iOS has no right-click). It is a
              // SIBLING button in a group — interactive content inside a <button> is invalid HTML.
              const showOnly = isMobile && !(on && selectedTechIds.length === 1);
              return (
                <div key={t.id} role="group" style={{display:"inline-flex",alignItems:"stretch"}}>
                <button onClick={()=>toggleTech(t.id)}
                  // Right-click (or shift-click) to isolate just this tech. iOS never fires
                  // contextmenu, so on a phone the chip carries an explicit "only" tap instead.
                  onContextMenu={e=>{e.preventDefault(); selectOnly(t.id);}}
                  title={isMobile ? undefined : `Click to toggle · right-click to view only ${t.name}`}
                  style={{
                    padding:isMobile?"8px 12px":"6px 12px",minHeight:isMobile?36:undefined,borderRadius: showOnly ? "20px 0 0 20px" : 20,cursor:"pointer",
                    border: on ? `2px solid ${NAVY}` : "1px solid #dfe1e6",
                    background: on ? NAVY : "white",
                    color:      on ? "white" : "#42526e",
                    fontSize:12,fontWeight:600,
                    display:"flex",alignItems:"center",gap:6,
                    transition: "all .12s",
                  }}>
                  <span>{t.name}</span>
                  {count > 0 && (
                    <span style={{
                      fontSize:10,fontWeight:700,padding:"1px 6px",borderRadius:10,
                      background: on ? "rgba(255,255,255,.25)" : "#f4f5f7",
                      color:      on ? "white" : "#42526e",
                    }}>{count}</span>
                  )}
                </button>
                {showOnly && (
                  <button type="button" aria-label={`View only ${t.name}`} onClick={()=>selectOnly(t.id)}
                    style={{
                      font:"inherit",fontSize:10,fontWeight:700,textTransform:"uppercase",letterSpacing:".04em",
                      padding:"0 10px",minHeight:36,cursor:"pointer",borderRadius:"0 20px 20px 0",
                      border: on ? `2px solid ${NAVY}` : "1px solid #dfe1e6", borderLeft:"none",
                      background: on ? NAVY : "white", color: on ? "rgba(255,255,255,.85)" : NAVY,
                    }}>only</button>
                )}
                </div>
              );
            })}
            {/* Unassigned tally — always visible if there are any */}
            {unassignedJobs.length > 0 && (
              <div style={{padding:"6px 12px",borderRadius:20,background:"#fef2f2",color:"#991b1b",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:6,border:"1px solid #fecaca"}}>
                ⚠ {unassignedJobs.length} unassigned
              </div>
            )}
          </div>
        </div>
        )}
      </div>

      {loadError && <div className="sc-error" role="alert"><span>{loadError} Retry to load visits for this date.</span><button className="btn btn-ghost btn-sm" onClick={() => setLoadRevision(value => value + 1)}>Retry routes</button></div>}

      {/* ── Per-tech sections ─────────────────────────────────────── */}
      {!loading && !loadError && selectedTechIds.length === 0 && (!controlledTech || (comparing && !showUnassigned)) && (
        <div className="empty"><p>Select one or more technicians above to see their routes.</p></div>
      )}

      {!loading && !loadError && visibleTechs.map(tech => {
        const { jobs, basis } = orderedJobs(tech, jobsForTech(tech.id));
        const completed = jobs.filter(j => j.status === "completed").length;
        const total = jobs.length;
        const pct = total ? Math.round((completed/total)*100) : 0;

        return (
          <div key={tech.id} className="card" style={{marginBottom:12,padding:0,overflow:"hidden"}}>
            {/* Tech header */}
            <div style={{padding:"12px 18px",background:"#f8faff",borderBottom:"1px solid #e3e8ef",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
              <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                <div style={{width:32,height:32,borderRadius:"50%",background:NAVY,color:GOLD,fontWeight:700,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontFamily:"Syne",flexShrink:0}}>
                  {tech.name.split(" ").map(s=>s[0]).join("").slice(0,2).toUpperCase()}
                </div>
                <div>
                  <div style={{fontWeight:700,fontSize:14,color:NAVY}}>{tech.name}</div>
                  <div style={{fontSize:11,color:"#8993a4"}}>
                    {completed}/{total} jobs done · {routeDate}
                    {basis && <span style={{marginLeft:6,fontWeight:600,color:basis==="road"?"#1a7f4b":"#8a93a2"}} title={basis==="road"?"Stops ordered by real driving distance":"Stops ordered by straight-line distance"}>· {basis==="road"?"🚗 driving order":"straight-line order"}</span>}
                  </div>
                </div>
              </div>
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap",...(isMobile?{flexBasis:"100%"}:{})}}>
                {/* Re-optimize: use after moving clients between techs. Cache-backed, so
                    pressing it on an unchanged route costs nothing. Owner-facing only —
                    a tech's own scoped view doesn't reorder the crew. */}
                {!techScoped && total > 0 && (
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={!!reopt}
                    onClick={() => reoptimize(tech)}
                    title="Re-run the driving optimizer for this tech and push the new order to their phone"
                    style={{whiteSpace:"nowrap"}}
                  >
                    {reopt === tech.id ? "Optimizing…" : "🔄 Re-optimize"}
                  </button>
                )}
                {total > 0 && (
                  <div style={{minWidth:isMobile?0:180,flex:isMobile?"1 1 100%":undefined,display:"flex",alignItems:"center",gap:8}}>
                    <div style={{flex:1,height:8,borderRadius:4,background:"#f0f2f5",overflow:"hidden"}}>
                      <div style={{width:`${pct}%`,height:"100%",background:"#36b37e",transition:"width .3s"}}/>
                    </div>
                    <span style={{fontSize:11,fontWeight:700,color:NAVY,minWidth:34,textAlign:"right"}}>{pct}%</span>
                  </div>
                )}
              </div>
            </div>

            {/* Job rows */}
            {jobs.length === 0 ? (
              <div style={{padding:"24px 18px",textAlign:"center",color:"#8993a4",fontSize:13}}>
                No jobs scheduled for {tech.name} on {routeDate}
              </div>
            ) : (
              <div>
                {jobs.map(job => (
                  <RouteJobRow
                    key={job.id}
                    job={job}
                    cleanMin={clientAvg[job.client_id]?.avgMin}
                    onOpen={()=>setJobDetailId(job.id)}
                    onStart={()=>handleStart(job)}
                    onComplete={()=>handleComplete(job)} onPhoto={(file)=>handlePhoto(job, file)}
                    onSkip={()=>setSkipModal({
                      id:        job.id,
                      visitId:   job.id,
                      clientId:  job.client_id,
                      date:      job.scheduled_date,
                      serviceType: job.service_type,
                    })}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Unassigned section — operator only (a technician has no business seeing
          or reassigning jobs that aren't theirs) */}
      {showUnassigned && !loading && unassignedJobs.length > 0 && (
        <div className="card" style={{marginBottom:12,padding:0,overflow:"hidden",border:"2px dashed #fecaca"}}>
          <div style={{padding:"12px 18px",background:"#fff5f5",borderBottom:"1px solid #fecaca"}}>
            <div style={{fontWeight:700,fontSize:14,color:"#991b1b"}}>⚠ Unassigned — {unassignedJobs.length} job{unassignedJobs.length===1?"":"s"}</div>
            <div style={{fontSize:11,color:"#7f1d1d",marginTop:2}}>These jobs aren't assigned to any technician. Click any to open and reassign.</div>
          </div>
          {unassignedJobs.map(job => (
            <RouteJobRow
              key={job.id}
              job={job}
              cleanMin={clientAvg[job.client_id]?.avgMin}
              onOpen={()=>setJobDetailId(job.id)}
              onStart={()=>handleStart(job)}
              onComplete={()=>handleComplete(job)} onPhoto={(file)=>handlePhoto(job, file)}
              onSkip={()=>setSkipModal({
                id:        job.id,
                visitId:   job.id,
                clientId:  job.client_id,
                firstName: job.client_name,   // B2: so the skip modal shows the client name
                date:      job.scheduled_date,
                serviceType: job.service_type,
              })}
            />
          ))}
        </div>
      )}
      {controlledTech && selectedTechnician === "unassigned" && !loading && unassignedJobs.length === 0 && (
        <div className="empty"><p>No unassigned visits on this date.</p></div>
      )}
      {controlledTech && selectedTechnician !== "unassigned" && !loading && visibleTechs.length === 0 && (!showUnassigned || unassignedJobs.length === 0) && (
        <div className="empty"><p>{selectedTechnician === "all" ? "No visits scheduled on this date." : "No visits match this technician."}</p></div>
      )}

      {/* Skip Modal */}
      {skipModal && (
        <RouteSkipModal
          job={skipModal}
          clients={clients}
          invoices={invoices}
          pricing={pricing}
          techId={skipModal.techId || ""}
          techName={skipModal.techName || ""}
          routeDate={routeDate}
          onClose={()=>setSkipModal(null)}
          onSkipped={async()=>{
            setSkipModal(null);
            const url = `${SUPABASE_URL}/rest/v1/jobs?scheduled_date=eq.${routeDate}&status=not.in.(cancelled,rescheduled)&order=client_name.asc&limit=500`;
            const res = await fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } }).then(r => r.json());
            if (Array.isArray(res)) setAllJobs(res);
            showToast && showToast("Visit skipped");
          }}
          showToast={showToast}
        />
      )}

      {/* Job detail modal */}
      {jobDetailId && (() => {
        const job = allJobs.find(j => j.id === jobDetailId);
        if (!job) { setJobDetailId(null); return null; }
        return (
          <JobDetailModal
            job={job}
            clients={clients}
            technicians={technicians}
            pricing={pricing}
            techScoped={techScoped}
            onClose={()=>setJobDetailId(null)}
            onUpdated={async()=>{
              // Refetch jobs to reflect any status/tech changes
              const url = `${SUPABASE_URL}/rest/v1/jobs?scheduled_date=eq.${routeDate}&status=not.in.(cancelled,rescheduled)&order=client_name.asc&limit=500`;
              const res = await fetch(url, { headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${getAuthToken()}` } }).then(r => r.json());
              if (Array.isArray(res)) setAllJobs(res);
              onRefresh && onRefresh();
            }}
            showToast={showToast}
          />
        );
      })()}
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Single job row inside a tech section. Action buttons stay on the
// row for fast operator workflow (no need to open the modal for the
// 80% case). Clicking anywhere outside the action buttons opens
// JobDetailModal for the full editor.
// ───────────────────────────────────────────────────────────────────
function RouteJobRow({ job, onOpen, onStart, onComplete, onSkip, onPhoto, cleanMin }) {
  const isMobile = useIsMobile();
  const isCompleted = job.status === "completed";
  const isStarted   = job.status === "started";
  const isSkipped   = job.status === "skipped" || job.status === "missed";

  const rowBg = isCompleted ? "#f0fdf4"
              : isSkipped   ? "#fafafa"
              : isStarted   ? "#eff6ff"
              :               "white";

  // Phone layout: the action cluster (Photo + Start/Complete + Skip ≈ 215px at max-content)
  // used to sit beside the name with flexShrink:0, crushing the name/address to ~80px.
  // On a phone the row wraps and the actions become a full-width line of 40px buttons
  // under the client name — a miss no longer lands on the row (which opens the modal).
  const btnStyle = isMobile
    ? {fontSize:13,padding:"10px 12px",minHeight:40,flex:"1 1 auto",justifyContent:"center"}
    : {fontSize:11};
  const btnCls = isMobile ? "btn" : "btn btn-sm";

  return (
    <div
      onClick={e => { if (e.target.closest("button,label")) return; onOpen(); }}
      style={{display:"flex",alignItems:"center",gap:12,padding:isMobile?"12px 14px":"12px 18px",borderBottom:"1px solid #f4f5f7",cursor:"pointer",background:rowBg,opacity:isSkipped?0.6:1,transition:"background .1s",...(isMobile?{flexWrap:"wrap",rowGap:10}:{})}}>
      <div style={{width:32,height:32,borderRadius:"50%",background:job.service_type==="deo"?"#f3e8ff":isCompleted?"#dcfce7":"#dbeafe",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,flexShrink:0}}>
        {job.service_type==="deo"?"💨":"🧹"}
      </div>
      <div style={{flex:1,minWidth:0}}>
        <div style={{fontWeight:600,fontSize:13,color:"#1a1f36",textDecoration:isSkipped?"line-through":"none"}}>
          {job.client_name}
        </div>
        <div style={{fontSize:11,color:"#42526e",marginTop:2}}>
          {job.client_address} · {job.dogs}🐕 · {job.service_type==="deo"?"Deodorizer":"Cleanup"}
        </div>
        {cleanMin!=null && (
          <div style={{fontSize:11,color:"#0369a1",marginTop:2,fontWeight:600}}>🧹 ~{cleanMin} min typical clean</div>
        )}
        {(isStarted && job.started_at) && (
          <div style={{fontSize:10,color:"#974f0c",marginTop:2}}>
            Started at {new Date(job.started_at).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}
          </div>
        )}
        {(isCompleted && job.completed_at) && (
          <div style={{fontSize:10,color:"#065f46",marginTop:2}}>
            Completed at {new Date(job.completed_at).toLocaleTimeString("en-US",{hour:"2-digit",minute:"2-digit"})}
          </div>
        )}
        {isSkipped && job.skipped_reason && (
          <div style={{fontSize:10,color:"#854d0e",marginTop:2}}>Skipped: {skipReasonLabel(job.skipped_reason)}{job.skip_note?` — ${job.skip_note}`:""}</div>
        )}
      </div>
      {(job.status === "scheduled" || job.status === "started" || (onPhoto && job.status !== "skipped")) && (
      <div style={isMobile
        ? {display:"flex",gap:8,flexBasis:"100%",alignItems:"center",flexWrap:"wrap",justifyContent:"flex-start",paddingLeft:44}
        : {display:"flex",gap:6,flexShrink:0,alignItems:"center",flexWrap:"wrap",justifyContent:"flex-end"}}>
        {onPhoto && job.status !== "skipped" && (
          <label className={`${btnCls} btn-ghost`} style={{...btnStyle,cursor:"pointer",whiteSpace:"nowrap",...(isMobile?{flex:"0 1 auto"}:{})}} title="Attach a proof-of-service photo" onClick={e=>e.stopPropagation()}>
            {job.photo_url ? "📸 ✓" : "📸 Photo"}
            <input type="file" accept="image/*" capture="environment" style={{display:"none"}}
              onChange={e=>{ const fl = e.target.files && e.target.files[0]; if (fl) onPhoto(fl); e.target.value=""; }}/>
          </label>
        )}
        {job.status === "scheduled" && (
          <>
            <button className={`${btnCls} btn-primary`} style={btnStyle} onClick={onStart}>▶ Start</button>
            <button className={`${btnCls} btn-ghost`} style={btnStyle} onClick={onSkip}>⊘ Skip</button>
          </>
        )}
        {job.status === "started" && (
          <>
            <button className={btnCls} style={{...btnStyle,background:"#36b37e",color:"white",border:"none"}} onClick={onComplete}>✓ Complete</button>
            <button className={`${btnCls} btn-ghost`} style={btnStyle} onClick={onSkip}>⊘ Skip</button>
          </>
        )}
      </div>
      )}
    </div>
  );
}
